import React, { useState, useEffect } from 'react';
import { FolderOpen, Trash2, Edit3, Download, Eye, Search, Filter, Calendar, DollarSign, Upload, FileDown, Trash } from 'lucide-react';
import { 
  loadInvoicesFromMemory, 
  deleteInvoiceFromMemory, 
  clearAllInvoices, 
  exportInvoicesData,
  importInvoicesData,
  getStorageInfo,
  showSaveNotification
} from '../utils/storage';
import { smartGeneratePDF } from '../utils/pdfGenerator';
import AdSense from './AdSense';

const SavedInvoices = ({ onEditInvoice, refreshTrigger }) => {
  const [savedInvoices, setSavedInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('lastModified');
  const [filterBy, setFilterBy] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [storageInfo, setStorageInfo] = useState(null);

  useEffect(() => {
    loadInvoices();
    updateStorageInfo();
  }, [refreshTrigger]);

  const loadInvoices = async () => {
    setIsLoading(true);
    try {
      const invoices = loadInvoicesFromMemory();
      setSavedInvoices(invoices);
      console.log(`Loaded ${invoices.length} invoices`);
    } catch (error) {
      console.error('Error loading invoices:', error);
      showSaveNotification('Failed to load invoices', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const updateStorageInfo = () => {
    const info = getStorageInfo();
    setStorageInfo(info);
  };

  const handleDelete = async (invoiceId) => {
    if (window.confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
      try {
        const success = deleteInvoiceFromMemory(invoiceId);
        if (success) {
          setSavedInvoices(loadInvoicesFromMemory());
          updateStorageInfo();
          showSaveNotification('Invoice deleted successfully');
        } else {
          showSaveNotification('Failed to delete invoice', 'error');
        }
      } catch (error) {
        console.error('Error deleting invoice:', error);
        showSaveNotification('Failed to delete invoice', 'error');
      }
    }
  };

  const handleDownload = async (invoice) => {
    try {
      const result = await smartGeneratePDF(invoice);
      if (result.success) {
        console.log(`PDF downloaded: ${result.filename}`);
        showSaveNotification('PDF downloaded successfully');
      } else {
        showSaveNotification('Failed to download PDF', 'error');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showSaveNotification('Failed to download PDF', 'error');
    }
  };

  const handleExportData = () => {
    try {
      const success = exportInvoicesData();
      if (success) {
        showSaveNotification('Data exported successfully');
      } else {
        showSaveNotification('Failed to export data', 'error');
      }
    } catch (error) {
      console.error('Export error:', error);
      showSaveNotification('Failed to export data', 'error');
    }
  };

  const handleImportData = (event) => {
    const file = event.target.files[0];
    if (file) {
      importInvoicesData(file)
        .then((count) => {
          if (count > 0) {
            loadInvoices();
            updateStorageInfo();
            showSaveNotification(`Imported ${count} invoices successfully`);
          }
        })
        .catch((error) => {
          console.error('Import error:', error);
          showSaveNotification('Failed to import data', 'error');
        });
    }
    // Reset file input
    event.target.value = '';
  };

  const handleClearAll = () => {
    const success = clearAllInvoices();
    if (success) {
      setSavedInvoices([]);
      updateStorageInfo();
      showSaveNotification('All invoices cleared');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    const currencySymbols = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      CAD: 'C$',
      AUD: 'A$'
    };
    return `${currencySymbols[currency] || '$'}${amount.toFixed(2)}`;
  };

  // Filter and sort invoices
  const filteredInvoices = savedInvoices
    .filter(invoice => {
      const matchesSearch = 
        invoice.invoiceDetails.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.clientInfo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.companyInfo.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (filterBy === 'all') return matchesSearch;
      if (filterBy === 'recent') {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        return matchesSearch && new Date(invoice.lastModified) > oneWeekAgo;
      }
      if (filterBy === 'high-value') {
        return matchesSearch && invoice.totals.total > 1000;
      }
      
      return matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'number':
          return a.invoiceDetails.number.localeCompare(b.invoiceDetails.number);
        case 'client':
          return a.clientInfo.name.localeCompare(b.clientInfo.name);
        case 'amount':
          return b.totals.total - a.totals.total;
        case 'date':
          return new Date(b.invoiceDetails.date) - new Date(a.invoiceDetails.date);
        default: // lastModified
          return new Date(b.lastModified) - new Date(a.lastModified);
      }
    });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your invoices...</p>
          </div>
        </div>
      </div>
    );
  }

  if (savedInvoices.length === 0) {
    return (
      <>
        {/* Mobile Responsive CSS */}
        <style jsx>{`
          @media (max-width: 768px) {
            .search-filters-container { flex-direction: column !important; }
            .filters-row { flex-direction: column !important; width: 100% !important; }
            .invoice-card-header { flex-direction: column !important; align-items: flex-start !important; }
            .invoice-card-actions { grid-template-columns: 1fr !important; }
          }
          .transition-all { transition: all 0.2s ease-in-out; }
          .group:hover { transform: translateY(-2px); }
          .transform:hover { transform: scale(1.02); }
          .animate-spin { animation: spin 1s linear infinite; }
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          .fade-in { animation: fadeIn 0.3s ease-in; }
          @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
          .notification { position: fixed; top: 1rem; right: 1rem; z-index: 50; padding: 1rem; border-radius: 0.5rem; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); color: white; transition: all 0.3s ease; }
          .notification.success { background-color: #10b981; }
          .notification.error { background-color: #ef4444; }
          @media (max-width: 640px) { .grid { grid-template-columns: 1fr; } }
          @media (min-width: 641px) and (max-width: 1024px) { .lg\\:grid-cols-3 { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
          ::-webkit-scrollbar { width: 6px; }
          ::-webkit-scrollbar-track { background: #f1f5f9; }
          ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
          ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
          @media print { .no-print { display: none !important; } }
          button:focus, input:focus, select:focus { outline: 2px solid #6366f1; outline-offset: 2px; }
          @media (prefers-contrast: high) { .bg-white { background-color: #ffffff; border: 1px solid #000000; } .text-gray-600 { color: #000000; } }
          @media (prefers-reduced-motion: reduce) { .transition-all, .transform, .animate-spin { animation: none; transition: none; transform: none; } }
          @media (prefers-color-scheme: dark) { .bg-gradient-to-br { background: linear-gradient(to bottom right, #1f2937, #374151); } }
        `}</style>

        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50 py-8">
          <div className="container mx-auto px-4">
            {/* AdSense - Only shows when loaded */}
            <AdSense adSlot="1234567890" adFormat="rectangle" />
            
            <div className="text-center py-20">
              <div className="bg-white/50 backdrop-blur-sm rounded-full p-8 w-32 h-32 mx-auto mb-6 flex items-center justify-center shadow-lg">
                <FolderOpen className="h-16 w-16 text-indigo-400" />
              </div>
              <h2 className="text-3xl font-bold text-gray-700 mb-4">No Saved Invoices</h2>
              <p className="text-gray-500 text-lg mb-4">Create your first invoice to see it here.</p>
              
              {/* Storage info */}
              {storageInfo && (
                <div className="bg-white/70 rounded-lg p-4 max-w-md mx-auto mb-6">
                  <p className="text-sm text-gray-600">
                    💾 Storage: {storageInfo.localStorageAvailable ? 'Available' : 'Not Available'}
                  </p>
                  {!storageInfo.localStorageAvailable && (
                    <p className="text-xs text-red-600 mt-1">
                      ⚠️ Data will not persist between sessions
                    </p>
                  )}
                </div>
              )}

              {/* Import option for empty state */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => window.location.reload()}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Create First Invoice
                </button>
                
                <label className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-3 rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl cursor-pointer flex items-center space-x-2">
                  <Upload className="w-4 h-4" />
                  <span>Import Backup</span>
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleImportData}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Mobile Responsive CSS */}
      <style jsx>{`
        @media (max-width: 768px) {
          .search-filters-container { flex-direction: column !important; }
          .filters-row { flex-direction: column !important; width: 100% !important; }
          .invoice-card-header { flex-direction: column !important; align-items: flex-start !important; }
          .invoice-card-actions { grid-template-columns: 1fr !important; }
        }
        .transition-all { transition: all 0.2s ease-in-out; }
        .group:hover { transform: translateY(-2px); }
        .transform:hover { transform: scale(1.02); }
        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .fade-in { animation: fadeIn 0.3s ease-in; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .notification { position: fixed; top: 1rem; right: 1rem; z-index: 50; padding: 1rem; border-radius: 0.5rem; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); color: white; transition: all 0.3s ease; }
        .notification.success { background-color: #10b981; }
        .notification.error { background-color: #ef4444; }
        @media (max-width: 640px) { .grid { grid-template-columns: 1fr; } }
        @media (min-width: 641px) and (max-width: 1024px) { .lg\\:grid-cols-3 { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        @media print { .no-print { display: none !important; } }
        button:focus, input:focus, select:focus { outline: 2px solid #6366f1; outline-offset: 2px; }
        @media (prefers-contrast: high) { .bg-white { background-color: #ffffff; border: 1px solid #000000; } .text-gray-600 { color: #000000; } }
        @media (prefers-reduced-motion: reduce) { .transition-all, .transform, .animate-spin { animation: none; transition: none; transform: none; } }
        @media (prefers-color-scheme: dark) { .bg-gradient-to-br { background: linear-gradient(to bottom right, #1f2937, #374151); } }
      `}</style>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50 py-8">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="mb-12 text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Saved Invoices</h1>
            <p className="text-gray-600 text-lg">Manage and edit your saved invoices</p>
            
            {/* Storage info */}
            {storageInfo && (
              <div className="mt-4 text-sm text-gray-500">
                📊 {storageInfo.invoiceCount} invoices • {storageInfo.storageSize} • 
                {storageInfo.localStorageAvailable ? ' ✅ Persistent' : ' ⚠️ Session only'}
              </div>
            )}
          </div>

          {/* Management Tools */}
          <div className="bg-white rounded-2xl shadow-lg border p-6 mb-8">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-800">Data Management</h3>
              
              <div className="flex flex-wrap gap-3">
                {/* Export Data */}
                <button
                  onClick={handleExportData}
                  className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all duration-200"
                >
                  <FileDown className="w-4 h-4" />
                  <span>Export</span>
                </button>

                {/* Import Data */}
                <label className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-all duration-200 cursor-pointer">
                  <Upload className="w-4 h-4" />
                  <span>Import</span>
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleImportData}
                    className="hidden"
                  />
                </label>

                {/* Clear All */}
                <button
                  onClick={handleClearAll}
                  className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-all duration-200"
                >
                  <Trash className="w-4 h-4" />
                  <span>Clear All</span>
                </button>
              </div>
            </div>

            {/* Search and Filter */}
            <div className="search-filters-container flex flex-col md:flex-row gap-4 items-center justify-between">
              {/* Search */}
              <div className="relative flex-1 max-w-md w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search invoices..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 focus:outline-none"
                />
              </div>

              {/* Filters */}
              <div className="filters-row flex flex-col md:flex-row gap-4 w-full md:w-auto">
                <div className="flex items-center space-x-2">
                  <Filter className="w-5 h-5 text-gray-500" />
                  <select
                    value={filterBy}
                    onChange={(e) => setFilterBy(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 focus:outline-none"
                  >
                    <option value="all">All Invoices</option>
                    <option value="recent">Recent (7 days)</option>
                    <option value="high-value">High Value ($1000+)</option>
                  </select>
                </div>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 focus:outline-none"
                >
                  <option value="lastModified">Last Modified</option>
                  <option value="date">Invoice Date</option>
                  <option value="number">Invoice Number</option>
                  <option value="client">Client Name</option>
                  <option value="amount">Amount</option>
                </select>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">{filteredInvoices.length}</div>
                <div className="text-sm text-gray-600">Total Invoices</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(filteredInvoices.reduce((sum, inv) => sum + inv.totals.total, 0))}
                </div>
                <div className="text-sm text-gray-600">Total Value</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {filteredInvoices.length > 0 ? formatCurrency(filteredInvoices.reduce((sum, inv) => sum + inv.totals.total, 0) / filteredInvoices.length) : '$0.00'}
                </div>
                <div className="text-sm text-gray-600">Average Value</div>
              </div>
            </div>
          </div>

          {/* AdSense - Only shows when loaded */}
          <AdSense adSlot="1234567891" adFormat="horizontal" />

          {/* Invoices Grid */}
          {filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No invoices match your search criteria.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredInvoices.map((invoice) => (
                <div key={invoice.id} className="bg-white rounded-2xl shadow-lg border hover:shadow-2xl transition-all duration-200 overflow-hidden group">
                  <div className="p-6">
                    {/* Header */}
                    <div className="invoice-card-header flex flex-col md:flex-row justify-between items-start mb-6 space-y-2 md:space-y-0">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">
                          {invoice.invoiceDetails.number}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {invoice.clientInfo.name || 'Unnamed Client'}
                        </p>
                      </div>
                      <div className="text-left md:text-right">
                        <p className="text-2xl font-bold text-indigo-600">
                          {formatCurrency(invoice.totals.total, invoice.currency)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(invoice.invoiceDetails.date)}
                        </p>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="space-y-2 text-sm text-gray-600 mb-6">
                      <div className="flex justify-between">
                        <span className="font-medium">From:</span>
                        <span className="truncate ml-2">{invoice.companyInfo.name || 'Your Business'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Items:</span>
                        <span>{invoice.items.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Currency:</span>
                        <span>{invoice.currency}</span>
                      </div>
                      {invoice.totals.taxRate > 0 && (
                        <div className="flex justify-between">
                          <span className="font-medium">Tax:</span>
                          <span>{invoice.totals.taxRate}%</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="font-medium">Modified:</span>
                        <span>{formatDate(invoice.lastModified)}</span>
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div className="mb-6">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        Saved {storageInfo?.localStorageAvailable ? '(Persistent)' : '(Session)'}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="invoice-card-actions grid grid-cols-1 md:grid-cols-2 gap-3">
                      <button
                        onClick={() => onEditInvoice(invoice)}
                        className="flex items-center justify-center space-x-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-3 rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                        <Edit3 className="w-4 h-4" />
                        <span>Edit</span>
                      </button>
                      
                      <button
                        onClick={() => handleDownload(invoice)}
                        className="flex items-center justify-center space-x-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-3 rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                        <Download className="w-4 h-4" />
                        <span>PDF</span>
                      </button>
                    </div>

                    {/* Delete Button */}
                    <button
                      onClick={() => handleDelete(invoice.id)}
                      className="w-full mt-3 flex items-center justify-center space-x-2 text-red-600 hover:bg-red-50 py-2 rounded-xl transition-all duration-200"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* AdSense - Only shows when loaded */}
          <AdSense adSlot="1234567893" adFormat="rectangle" />
        </div>
      </div>
    </>
  );
};

export default SavedInvoices;